import { Badge } from "@mantine/core";
import { useSessionStorage } from "@mantine/hooks";
import { IconTimelineEvent } from "@tabler/icons-react";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { getMinimalMRTOptions } from "../../../mantine/getMinimalMRTOptions";
import { useStartPageOnboardingProgress } from "../hooks/useStartPageOnboardingProgress";
import { useStartPageSites } from "../hooks/useStartPageSites";
import type { StartPageOnboardingProgress } from "../StartPage.types";
import { Tile } from "./Tile";

interface OnboardingProgressTileProps {
  projectId?: string;
}

export const startPageOnboardingSiteIdSessionStorageKey =
  "startPageOnboardingSiteId";

function OnboardingProgressTile({ projectId }: OnboardingProgressTileProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [storedOnboardingSiteId, setStoredOnboardingSiteId] = useSessionStorage<
    number | undefined
  >({
    key: startPageOnboardingSiteIdSessionStorageKey,
    defaultValue: undefined
  });

  const { sites } = useStartPageSites(projectId);
  const {
    data: onboardingProgress,
    error: onboardingProgressError,
    isLoading: onboardingProgressLoading
  } = useStartPageOnboardingProgress(
    currentPage,
    pageSize,
    projectId,
    storedOnboardingSiteId
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [projectId, storedOnboardingSiteId]);

  useEffect(() => {
    if (
      sites &&
      storedOnboardingSiteId &&
      !sites?.find((site) => site.id === storedOnboardingSiteId)
    ) {
      setStoredOnboardingSiteId(undefined);
    }
  }, [sites, setStoredOnboardingSiteId, storedOnboardingSiteId]);

  function handleSiteChange(newSiteId: number | undefined) {
    setCurrentPage(1);
    setStoredOnboardingSiteId(newSiteId);
  }

  function handleSwitchPage(newCurrentPage: number, newPageSize: number) {
    setCurrentPage(newCurrentPage);
    setPageSize(newPageSize);
  }

  const allSitesChoice = { value: undefined, label: "Alle Liegenschaften" };
  const sitesOptions = sites
    ? [
        allSitesChoice,
        ...sites.map((site) => ({
          label: site.name,
          value: site.id
        }))
      ]
    : [];

  const mrtColumns: Array<MRT_ColumnDef<StartPageOnboardingProgress>> = useMemo(
    () => [
      { header: "Liegenschaftsname", accessorKey: "site_name" },
      {
        header: "Phase",
        accessorKey: "phase_name",
        Cell: ({ row }) => (
          <Link
            to={`../projekte/${row.original.project_id}/liegenschaften/${row.original.site_id}`}
          >
            {row.original.phase_name}
          </Link>
        )
      },
      {
        header: "Status",
        accessorKey: "phaseStatus",
        size: 90,
        grow: false,
        Cell: () => (
          <Badge color="warning" miw={60} size="sm">
            Offen
          </Badge>
        )
      }
    ],
    []
  );

  const mrtTable = useMantineReactTable<StartPageOnboardingProgress>({
    ...getMinimalMRTOptions({
      emptyRowsFallbackText: "Super, alles erledigt!"
    }),
    state: {
      isLoading: onboardingProgressLoading,
      pagination: { pageIndex: currentPage - 1, pageSize: pageSize }
    },
    mantineTableHeadRowProps: { display: "none" },
    layoutMode: "grid",
    enableBottomToolbar: true,
    enablePagination: true,
    manualPagination: true,
    paginationDisplayMode: "pages",
    autoResetPageIndex: false,
    onPaginationChange: (pagination) => {
      if (typeof pagination === "function") {
        const currentState = { pageIndex: currentPage - 1, pageSize };
        const newState = pagination(currentState);
        handleSwitchPage(newState.pageIndex + 1, newState.pageSize);
      }
    },
    mantinePaginationProps: { size: "xs" },
    rowCount: onboardingProgress ? onboardingProgress.count : 0,
    columns: mrtColumns,
    data: onboardingProgress ? onboardingProgress.results : []
  });

  return (
    <Tile
      error={onboardingProgressError}
      filterOptions={sitesOptions}
      filterPlaceholder="Auswahl der Liegenschaft"
      filterValue={storedOnboardingSiteId}
      icon={IconTimelineEvent}
      title="Onboarding-Fortschritt"
      onFilterChange={handleSiteChange}
    >
      <MantineReactTable table={mrtTable} />
    </Tile>
  );
}

export { OnboardingProgressTile, OnboardingProgressTileProps };
